/**
 * SSO Callback Page
 * ==================
 * Handles SSO token exchange from legacy system.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';

import { useSSOExchange } from '@/hooks/useAuth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

export default function SSOCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mutate: exchangeSSO, isPending, isError, error } = useSSOExchange();
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (attempted) return;

    const ssoToken = searchParams.get('token');
    const patientId = searchParams.get('patient_id');

    if (ssoToken) {
      setAttempted(true);
      exchangeSSO({
        sso_token: ssoToken,
        patient_id: patientId || undefined,
      });
    } else {
      // No token provided
      navigate('/login', { replace: true });
    }
  }, [searchParams, exchangeSSO, navigate, attempted]);

  // Error state
  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Failed
          </h2>
          <p className="text-gray-600 mb-6">
            {(error as any)?.response?.data?.error?.message ||
              'Unable to authenticate with your account. Please try again or contact support.'}
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Go to Login
            </Button>
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              className="w-full"
            >
              Go Back
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="max-w-md w-full p-8 text-center">
        <Loader2 className="w-12 h-12 mx-auto text-primary-600 animate-spin mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Signing you in...
        </h2>
        <p className="text-gray-600">
          Please wait while we authenticate your session.
        </p>
      </Card>
    </div>
  );
}
